import { t } from 'i18next';
import { store } from '../store';
import { setLocation } from '../store/slices/locationSlice';

export interface LocationResult {
  latitude: number;
  longitude: number;
}

export const requestTelegramLocation = (
  onSuccess?: (location: LocationResult) => void,
  onError?: () => void
): void => {
  const commit = (location: LocationResult) => {
    const locationData = {
      latitude: location.latitude,
      longitude: location.longitude,
    };

    store.dispatch(setLocation(locationData));
    sessionStorage.setItem('location', JSON.stringify(locationData));

    onSuccess?.(locationData);
  };

  /**
   * Outside Telegram there is no LocationManager, and returning early left
   * every caller with a dead button — most visibly the "my location" control
   * on the address screens during local development, where the app runs in a
   * plain browser tab. The standard geolocation API covers that case.
   */
  const requestBrowserGeolocation = () => {
    if (!navigator.geolocation) {
      onError?.();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position =>
        commit({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      () => onError?.(),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const WebApp = window.Telegram?.WebApp;
  const LM = WebApp?.LocationManager;

  if (!WebApp || !LM) {
    requestBrowserGeolocation();
    return;
  }

  const getLocation = (onNull: () => void) => {
    LM.getLocation((location: LocationResult | null) => {
      if (location) {
        commit(location);
        return;
      }

      onNull();
    });
  };

  const runLogic = () => {
    if (LM.isAccessGranted) {
      getLocation(() => onError?.());
      return;
    }

    WebApp.showConfirm(t('location-request'), (ok: boolean) => {
      if (!ok) {
        onError?.();
        return;
      }

      getLocation(() => {
        WebApp.showAlert(t('location-error'));
        onError?.();
      });
    });
  };

  if (!LM.isInited) {
    LM.init(() => runLogic());
  } else {
    runLogic();
  }
};
